import React, { useState, useMemo } from 'react';

// ── Colors & shared styles ────────────────────────────────────────────────────
const C = {
  bg:'#0a0a0c', bg2:'#111114', bg3:'#18181c',
  gold:'#c9a84c', goldL:'#e8c97a',
  sage:'#7aab8a', rose:'#c47a7a', sky:'#7a9ec4',
  text:'#f0ede6', muted:'rgba(240,237,230,0.5)', dim:'rgba(240,237,230,0.22)',
  border:'rgba(201,168,76,0.15)',
};
const serif  = { fontFamily:"'Cormorant Garamond', serif" };
const eyebrow = { fontSize:'10px', letterSpacing:'0.12em', color:C.gold, textTransform:'uppercase', fontWeight:500 };
const card   = { background:C.bg2, border:`0.5px solid ${C.border}`, borderRadius:'10px', padding:'14px 16px', marginBottom:'10px' };
const btnTab = a => ({ padding:'8px 18px', background: a ? 'rgba(201,168,76,0.12)' : 'transparent', border:`0.5px solid ${a ? 'rgba(201,168,76,0.4)' : C.border}`, borderRadius:'7px', color: a ? C.goldL : C.muted, fontSize:'12px', cursor:'pointer' });
const inp    = { padding:'9px 12px', background:C.bg3, border:`0.5px solid ${C.border}`, borderRadius:'7px', color:C.text, fontSize:'13px', outline:'none', width:'100%', boxSizing:'border-box', transition:'border 0.2s' };
const btnG   = { padding:'9px 20px', background:'rgba(201,168,76,0.1)', border:'0.5px solid rgba(201,168,76,0.4)', borderRadius:'7px', color:C.goldL, fontSize:'12px', cursor:'pointer' };

// ── Formatters ────────────────────────────────────────────────────────────────
const fmtD  = n => (typeof n === 'number' && isFinite(n)) ? n.toLocaleString('en-US', {minimumFractionDigits:2, maximumFractionDigits:2}) : '—';
const fmtS  = n => (typeof n === 'number' && isFinite(n)) ? (n >= 0 ? '+$' : '-$') + fmtD(Math.abs(n)) : '—';
const fmtP  = n => (typeof n === 'number' && isFinite(n)) ? n.toFixed(2) + '%' : '—';
const num   = s => { const v = parseFloat(s); return isNaN(v) ? 0 : v; };

// ── Input row helper ──────────────────────────────────────────────────────────
function Field({ label, value, onChange, type = 'number', step = 'any', min, children, tip }) {
  return (
    <div style={{ marginBottom:'10px' }}>
      <div style={{ fontSize:'11px', color:C.muted, marginBottom:'4px', display:'flex', alignItems:'center', gap:'4px' }}>
        {label}
        {tip && <span title={tip} style={{ fontSize:'10px', color:C.dim, cursor:'help' }}>ⓘ</span>}
      </div>
      {children || (
        <input type={type} step={step} min={min} value={value} onChange={e => onChange(e.target.value)}
          style={inp} onFocus={e => e.target.style.borderColor = 'rgba(201,168,76,0.5)'}
          onBlur={e => e.target.style.borderColor = C.border} />
      )}
    </div>
  );
}

function Select({ label, value, onChange, options }) {
  return (
    <div style={{ marginBottom:'10px' }}>
      <div style={{ fontSize:'11px', color:C.muted, marginBottom:'4px' }}>{label}</div>
      <select value={value} onChange={e => onChange(e.target.value)} style={{ ...inp }}>
        {options.map(o => <option key={o.value || o} value={o.value || o}>{o.label || o}</option>)}
      </select>
    </div>
  );
}

function Result({ label, value, color = C.text, sub }) {
  return (
    <div style={{ ...card, marginBottom:'8px' }}>
      <div style={{ ...eyebrow, marginBottom:'3px' }}>{label}</div>
      <div style={{ fontSize:'20px', fontWeight:500, color, ...serif }}>{value}</div>
      {sub && <div style={{ fontSize:'11px', color:C.muted, marginTop:'2px' }}>{sub}</div>}
    </div>
  );
}

function TwoCol({ left, right }) {
  return (
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'16px', alignItems:'start' }}>
      <div>{left}</div><div>{right}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CALCULATOR 1 — OPTIONS P&L
// ─────────────────────────────────────────────────────────────────────────────
function PLChart({ tradeType, stockEntry, strike, premium, contracts, expiryPrice }) {
  const [se, st, pr, ct, ep] = [num(stockEntry), num(strike), num(premium), num(contracts) || 1, num(expiryPrice)];
  if (!se || !st || !pr) return null;

  const pnlAt = price => {
    switch (tradeType) {
      case 'cc':  return (Math.min(price, st) - se + pr) * 100 * ct;
      case 'csp': return (pr - Math.max(0, st - price)) * 100 * ct;
      case 'lc':  return (Math.max(0, price - st) - pr) * 100 * ct;
      case 'lp':  return (Math.max(0, st - price) - pr) * 100 * ct;
      default:    return 0;
    }
  };

  const low   = Math.max(0.01, Math.min(se, st) * 0.78);
  const high  = Math.max(se, st) * 1.22;
  const steps = 120;
  const pts   = Array.from({ length: steps + 1 }, (_, i) => {
    const price = low + (high - low) * (i / steps);
    return { price, pnl: pnlAt(price) };
  });

  const maxPnl = Math.max(...pts.map(p => p.pnl));
  const minPnl = Math.min(...pts.map(p => p.pnl));
  const range  = maxPnl - minPnl || 1;
  const W = 480, H = 160, PAD = { t:10, b:24, l:8, r:8 };
  const gW = W - PAD.l - PAD.r, gH = H - PAD.t - PAD.b;

  const toX = price => PAD.l + ((price - low) / (high - low)) * gW;
  const toY = pnl   => PAD.t + (1 - (pnl - minPnl) / range) * gH;

  const zeroY = toY(0);
  const breakeven = (() => {
    for (let i = 1; i < pts.length; i++) {
      if ((pts[i-1].pnl < 0 && pts[i].pnl >= 0) || (pts[i-1].pnl >= 0 && pts[i].pnl < 0)) {
        return (pts[i-1].price + pts[i].price) / 2;
      }
    }
    return null;
  })();

  const pathData = (filterFn, color) => {
    let d = '', open = false;
    pts.forEach((p, i) => {
      if (filterFn(p.pnl)) {
        if (!open) { d += `M ${toX(p.price).toFixed(1)} ${toY(p.pnl).toFixed(1)} `; open = true; }
        else        { d += `L ${toX(p.price).toFixed(1)} ${toY(p.pnl).toFixed(1)} `; }
      } else open = false;
    });
    return d ? <path d={d} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" /> : null;
  };

  const curX = ep ? toX(ep) : null;
  const curPnl = ep ? pnlAt(ep) : null;

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display:'block', background:C.bg3, borderRadius:'8px', marginTop:'10px' }}>
      {/* Zero line */}
      <line x1={PAD.l} y1={zeroY} x2={W - PAD.r} y2={zeroY} stroke="rgba(240,237,230,0.15)" strokeWidth="1" strokeDasharray="4 3" />
      {/* P&L curves */}
      {pathData(v => v >= 0, C.sage)}
      {pathData(v => v <  0, C.rose)}
      {/* Breakeven */}
      {breakeven && (
        <>
          <line x1={toX(breakeven)} y1={PAD.t} x2={toX(breakeven)} y2={H - PAD.b} stroke="rgba(201,168,76,0.5)" strokeWidth="1" strokeDasharray="4 3" />
          <text x={toX(breakeven) + 3} y={PAD.t + 10} fill={C.gold} fontSize="9" fontFamily="'DM Mono', monospace">BE ${breakeven.toFixed(2)}</text>
        </>
      )}
      {/* Current price dot */}
      {curX && curPnl !== null && (
        <>
          <circle cx={curX} cy={toY(curPnl)} r="4" fill={curPnl >= 0 ? C.sage : C.rose} />
          <text x={curX + 6} y={toY(curPnl) - 4} fill={curPnl >= 0 ? C.sage : C.rose} fontSize="9" fontFamily="'DM Mono', monospace">
            {fmtS(curPnl)}
          </text>
        </>
      )}
      {/* Axis labels */}
      <text x={PAD.l}   y={H - 5} fill={C.dim} fontSize="9" fontFamily="'DM Mono', monospace">${low.toFixed(0)}</text>
      <text x={W / 2}   y={H - 5} fill={C.dim} fontSize="9" fontFamily="'DM Mono', monospace" textAnchor="middle">${((low + high) / 2).toFixed(0)}</text>
      <text x={W - PAD.r} y={H - 5} fill={C.dim} fontSize="9" fontFamily="'DM Mono', monospace" textAnchor="end">${high.toFixed(0)}</text>
      <text x={PAD.l + 4} y={PAD.t + 9}  fill={C.sage} fontSize="9" fontFamily="'DM Mono', monospace">Max ${fmtD(maxPnl)}</text>
      {minPnl < 0 && <text x={PAD.l + 4} y={H - PAD.b - 4} fill={C.rose} fontSize="9" fontFamily="'DM Mono', monospace">-${fmtD(Math.abs(minPnl))}</text>}
    </svg>
  );
}

function Calc1() {
  const [tt, setTt]   = useState('csp');
  const [se, setSe]   = useState('50');
  const [st, setSt]   = useState('47');
  const [pr, setPr]   = useState('2.10');
  const [ct, setCt]   = useState('1');
  const [ep, setEp]   = useState('');

  const R = useMemo(() => {
    const [SE, ST, PR, CT] = [num(se), num(st), num(pr), num(ct) || 1];
    if (!SE || !ST || !PR) return null;
    const tot = 100 * CT;
    switch (tt) {
      case 'cc':  return { maxProfit: (ST - SE + PR) * tot, maxLoss: (SE - PR) * tot, breakeven: SE - PR, roc: (PR / SE) * 100 };
      case 'csp': return { maxProfit: PR * tot, maxLoss: (ST - PR) * tot, breakeven: ST - PR, roc: (PR / ST) * 100 };
      case 'lc':  return { maxProfit: null, maxLoss: PR * tot, breakeven: ST + PR, roc: null };
      case 'lp':  return { maxProfit: (ST - PR) * tot, maxLoss: PR * tot, breakeven: ST - PR, roc: null };
      default:    return null;
    }
  }, [tt, se, st, pr, ct]);

  const pnlAtExpiry = useMemo(() => {
    if (!ep || !R) return null;
    const EP = num(ep), ST = num(st), PR = num(pr), SE = num(se), CT = num(ct) || 1;
    switch (tt) {
      case 'cc':  return (Math.min(EP, ST) - SE + PR) * 100 * CT;
      case 'csp': return (PR - Math.max(0, ST - EP)) * 100 * CT;
      case 'lc':  return (Math.max(0, EP - ST) - PR) * 100 * CT;
      case 'lp':  return (Math.max(0, ST - EP) - PR) * 100 * CT;
      default:    return null;
    }
  }, [ep, tt, st, pr, se, ct]);

  const tradeTypes = [{ value:'cc', label:'Covered Call' }, { value:'csp', label:'Cash-Secured Put' }, { value:'lc', label:'Long Call' }, { value:'lp', label:'Long Put' }];

  return (
    <TwoCol
      left={<>
        <Select label="Trade Type" value={tt} onChange={setTt} options={tradeTypes} />
        <Field label="Stock Price at Entry ($)" value={se} onChange={setSe} />
        <Field label="Strike Price ($)" value={st} onChange={setSt} />
        <Field label="Premium Received/Paid ($)" value={pr} onChange={setPr} />
        <Field label="Number of Contracts" value={ct} onChange={setCt} min="1" />
        <Field label="Stock Price at Expiration (optional)" value={ep} onChange={setEp} tip="Enter an expiry price to see P&L at that price" />
        <PLChart tradeType={tt} stockEntry={se} strike={st} premium={pr} contracts={ct} expiryPrice={ep} />
      </>}
      right={<>
        {R ? <>
          <Result label="Max Profit" value={R.maxProfit !== null ? `$${fmtD(R.maxProfit)}` : 'Unlimited'} color={C.sage}
            sub={R.maxProfit !== null ? `$${fmtD(R.maxProfit / (num(ct)||1))} per contract` : undefined} />
          <Result label="Max Loss" value={`-$${fmtD(R.maxLoss)}`} color={C.rose}
            sub={`$${fmtD(R.maxLoss / (num(ct)||1))} per contract`} />
          <Result label="Breakeven Price" value={`$${fmtD(R.breakeven)}`} />
          {R.roc !== null && <Result label="Return on Capital" value={fmtP(R.roc)} color={C.gold} />}
          {pnlAtExpiry !== null && <Result label={`P&L at $${ep}`} value={fmtS(pnlAtExpiry)} color={pnlAtExpiry >= 0 ? C.sage : C.rose} />}
        </> : <div style={{ color:C.dim, fontSize:'13px', marginTop:'20px', textAlign:'center' }}>Enter values to see results</div>}
      </>}
    />
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CALCULATOR 2 — COVERED CALL
// ─────────────────────────────────────────────────────────────────────────────
function Calc2() {
  const [sh, setSh]   = useState('100');
  const [cb, setCb]   = useState('45');
  const [sp, setSp]   = useState('48');
  const [cs, setCs]   = useState('52');
  const [pr, setPr]   = useState('1.85');
  const [dte, setDte] = useState('30');

  const R = useMemo(() => {
    const [SH, CB, SP, CS, PR, DTE] = [num(sh), num(cb), num(sp), num(cs), num(pr), num(dte)];
    if (!SH || !CB || !CS || !PR) return null;
    const totalPrem     = PR * SH;
    const newCB         = CB - PR;
    const cbReductPct   = (PR / CB) * 100;
    const maxGain       = (CS - CB + PR) * SH;
    const maxGainPct    = (maxGain / (CB * SH)) * 100;
    const annualized    = DTE > 0 ? (PR / CB) * (365 / DTE) * 100 : 0;
    const downside      = (PR / CB) * 100;
    const calledAwayPnL = (CS - CB + PR) * SH;
    const notCalledPnL  = PR * SH;
    const monthly       = DTE > 0 ? totalPrem * (30 / DTE) : totalPrem;
    return { totalPrem, newCB, cbReductPct, maxGain, maxGainPct, annualized, downside, calledAwayPnL, notCalledPnL, monthly };
  }, [sh, cb, sp, cs, pr, dte]);

  return (
    <TwoCol
      left={<>
        <Field label="Shares Owned" value={sh} onChange={setSh} min="1" />
        <Field label="Original Cost Basis per Share ($)" value={cb} onChange={setCb} />
        <Field label="Current Stock Price ($)" value={sp} onChange={setSp} />
        <Field label="Call Strike Price ($)" value={cs} onChange={setCs} />
        <Field label="Premium Received per Share ($)" value={pr} onChange={setPr} />
        <Field label="Days to Expiration" value={dte} onChange={setDte} min="1" />
      </>}
      right={R ? <>
        <Result label="Total Premium Income" value={`$${fmtD(R.totalPrem)}`} color={C.gold} />
        <Result label="New Effective Cost Basis" value={`$${fmtD(R.newCB)}`} sub={`${fmtP(R.cbReductPct)} reduction from $${cb}`} color={C.sage} />
        <Result label="Maximum Gain if Called Away" value={`$${fmtD(R.maxGain)}`} sub={fmtP(R.maxGainPct)} color={C.sage} />
        <Result label="Annualized Return" value={fmtP(R.annualized)} color={C.gold} />
        <Result label="Downside Protection" value={fmtP(R.downside)} />
        <Result label="Monthly Income Projection" value={`$${fmtD(R.monthly)}`} sub="if same trade repeated" color={C.sky} />
        <div style={{ ...card, background:'rgba(122,171,138,0.06)', marginBottom:'8px' }}>
          <div style={{ ...eyebrow, marginBottom:'6px' }}>Scenario Outcomes</div>
          <div style={{ display:'flex', justifyContent:'space-between', fontSize:'12px', marginBottom:'4px' }}>
            <span style={{ color:C.muted }}>If called away:</span>
            <span style={{ color:C.sage }}>{fmtS(R.calledAwayPnL)}</span>
          </div>
          <div style={{ display:'flex', justifyContent:'space-between', fontSize:'12px' }}>
            <span style={{ color:C.muted }}>If not called away:</span>
            <span style={{ color:C.gold }}>Keep ${fmtD(R.notCalledPnL)} premium, sell again</span>
          </div>
        </div>
      </> : <div style={{ color:C.dim, fontSize:'13px', marginTop:'20px', textAlign:'center' }}>Enter values to see results</div>}
    />
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CALCULATOR 3 — CASH-SECURED PUT
// ─────────────────────────────────────────────────────────────────────────────
function Calc3() {
  const [sp, setSp]   = useState('50');
  const [st, setSt]   = useState('47');
  const [pr, setPr]   = useState('2.10');
  const [ct, setCt]   = useState('1');
  const [dte, setDte] = useState('30');

  const R = useMemo(() => {
    const [SP, ST, PR, CT, DTE] = [num(sp), num(st), num(pr), num(ct)||1, num(dte)];
    if (!ST || !PR) return null;
    const cash   = ST * 100 * CT;
    const income = PR * 100 * CT;
    const effPx  = ST - PR;
    const annual = DTE > 0 ? (PR / ST) * (365 / DTE) * 100 : 0;
    const roew   = (PR / ST) * 100;
    const maxLoss = (ST - PR) * 100 * CT;
    return { cash, income, effPx, annual, breakeven: ST - PR, roew, maxLoss };
  }, [sp, st, pr, ct, dte]);

  return (
    <TwoCol
      left={<>
        <Field label="Stock Price ($)" value={sp} onChange={setSp} />
        <Field label="Put Strike Price ($)" value={st} onChange={setSt} />
        <Field label="Premium Received per Share ($)" value={pr} onChange={setPr} />
        <Field label="Number of Contracts" value={ct} onChange={setCt} min="1" />
        <Field label="Days to Expiration" value={dte} onChange={setDte} min="1" />
        {R && <div style={{ padding:'10px 12px', background:C.bg3, borderRadius:'7px', fontSize:'12px', color:C.muted, marginTop:'4px' }}>
          Cash Required: <span style={{ color:C.text }}>${fmtD(R.cash)}</span> (auto-calculated: {st} × 100 × {ct})
        </div>}
      </>}
      right={R ? <>
        <Result label="Total Premium Income" value={`$${fmtD(R.income)}`} color={C.gold} />
        <Result label="Cash Required" value={`$${fmtD(R.cash)}`} />
        <Result label="Effective Purchase Price if Assigned" value={`$${fmtD(R.effPx)}`} color={C.sky} sub="Your real cost if you get the shares" />
        <Result label="Annualized Return on Cash" value={fmtP(R.annual)} color={C.gold} />
        <Result label="Breakeven Price" value={`$${fmtD(R.breakeven)}`} />
        <Result label="Return if Expires Worthless" value={fmtP(R.roew)} color={C.sage} />
        <Result label="Maximum Loss (stock → $0)" value={`-$${fmtD(R.maxLoss)}`} color={C.rose} />
        <div style={{ ...card, background:'rgba(122,171,138,0.06)', fontSize:'12px', color:C.muted, lineHeight:1.6 }}>
          💡 If you sold at the 30-delta strike, you have approximately a <span style={{ color:C.sage }}>70% probability</span> of this expiring worthless.
        </div>
      </> : <div style={{ color:C.dim, fontSize:'13px', marginTop:'20px', textAlign:'center' }}>Enter values to see results</div>}
    />
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CALCULATOR 4 — THE WHEEL
// ─────────────────────────────────────────────────────────────────────────────
function Calc4() {
  const [cap,  setCap]  = useState('25000');
  const [tick, setTick] = useState('AAPL');
  const [sp,   setSp]   = useState('50');
  const [cspP, setCspP] = useState('2.10');
  const [ccP,  setCcP]  = useState('1.85');
  const [dte,  setDte]  = useState('30');
  const [ct,   setCt]   = useState('1');

  const R = useMemo(() => {
    const [SP, CP, CCP, DTE, CT, CAP] = [num(sp), num(cspP), num(ccP), num(dte)||30, num(ct)||1, num(cap)];
    if (!SP || !CP) return null;
    const avgPrem  = (CP + CCP) / 2;
    const monthly  = avgPrem * 100 * CT * (30 / DTE);
    const annual   = monthly * 12;
    const rocMo    = CAP > 0 ? (monthly / CAP) * 100 : 0;
    const rocAnn   = CAP > 0 ? (annual  / CAP) * 100 : 0;
    const startCB  = SP * 100 * CT;
    const months   = Math.ceil(startCB / monthly);
    const table    = Array.from({ length: 12 }, (_, i) => ({
      month: i + 1,
      prem:  monthly,
      cumul: monthly * (i + 1),
      basis: Math.max(0, startCB - monthly * (i + 1)),
    }));
    return { monthly, annual, rocMo, rocAnn, startCB, months, table };
  }, [sp, cspP, ccP, dte, ct, cap]);

  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'16px', alignItems:'start' }}>
        <div>
          <Field label="Starting Capital ($)" value={cap} onChange={setCap} />
          <Field label="Target Ticker (label only)" value={tick} onChange={setTick} type="text" />
          <Field label="Target Stock Price ($)" value={sp} onChange={setSp} />
          <Field label="CSP Premium per Share ($)" value={cspP} onChange={setCspP} />
          <Field label="CC Premium per Share ($)" value={ccP} onChange={setCcP} />
          <Field label="Average DTE" value={dte} onChange={setDte} min="1" />
          <Field label="Contracts" value={ct} onChange={setCt} min="1" />
        </div>
        <div>
          {R ? <>
            <Result label="Monthly Income Estimate" value={`$${fmtD(R.monthly)}`} color={C.gold} />
            <Result label="Annual Income Estimate" value={`$${fmtD(R.annual)}`} color={C.gold} />
            <Result label="Monthly ROC" value={fmtP(R.rocMo)} color={C.sage} sub={`${fmtP(R.rocAnn)} annualized`} />
            <Result label={`Months to Own ${tick || 'Stock'} Free`} value={isFinite(R.months) ? `${R.months} months` : '—'} color={C.sky} sub="until premium collected = cost of shares" />
          </> : <div style={{ color:C.dim, fontSize:'13px', textAlign:'center', paddingTop:'20px' }}>Enter values to see results</div>}
        </div>
      </div>
      {R && (
        <div style={{ marginTop:'16px' }}>
          <div style={{ ...eyebrow, marginBottom:'10px' }}>12-Month Projection — {tick}</div>
          <div style={{ borderRadius:'10px', overflow:'hidden', border:`0.5px solid ${C.border}` }}>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:'1px', background:C.bg3, padding:'8px 14px', fontSize:'10px', color:C.dim, letterSpacing:'0.08em' }}>
              {['MONTH', 'PREMIUM', 'CUMULATIVE', 'COST BASIS'].map(h => <span key={h}>{h}</span>)}
            </div>
            {R.table.map((row, i) => (
              <div key={i} style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:'1px', padding:'8px 14px', background: i % 2 ? C.bg3 : C.bg2, fontSize:'12px', borderBottom:`0.5px solid rgba(240,237,230,0.03)` }}>
                <span style={{ color:C.muted }}>Month {row.month}</span>
                <span style={{ color:C.sage }}>${fmtD(row.prem)}</span>
                <span style={{ color:C.gold }}>${fmtD(row.cumul)}</span>
                <span style={{ color: row.basis === 0 ? C.sage : C.text, fontWeight: row.basis === 0 ? 600 : 400 }}>
                  {row.basis === 0 ? 'FREE 🎉' : `$${fmtD(row.basis)}`}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CALCULATOR 5 — POSITION SIZE
// ─────────────────────────────────────────────────────────────────────────────
function Calc5() {
  const [acct, setAcct] = useState('25000');
  const [risk, setRisk] = useState('2');
  const [sp,   setSp]   = useState('50');
  const [st,   setSt]   = useState('47');
  const [pr,   setPr]   = useState('2.10');

  const R = useMemo(() => {
    const [ACCT, RISK, ST, PR] = [num(acct), num(risk), num(st), num(pr)];
    if (!ACCT || !ST || !PR) return null;
    const maxRisk      = ACCT * RISK / 100;
    const riskPerCt    = (ST - PR) * 100;
    const maxContracts = riskPerCt > 0 ? Math.floor(maxRisk / riskPerCt) : 0;
    const cashReq      = ST * 100 * maxContracts;
    const deployed     = ACCT > 0 ? (cashReq / ACCT) * 100 : 0;
    return { maxRisk, maxContracts, cashReq, deployed };
  }, [acct, risk, st, pr]);

  return (
    <TwoCol
      left={<>
        <Field label="Total Account Size ($)" value={acct} onChange={setAcct} />
        <Field label={`Max Risk Per Trade: ${risk}%`} value={risk} onChange={setRisk} tip="Most professional sellers risk 2–3% per trade">
          <input type="range" min="1" max="5" step="0.5" value={risk}
            onChange={e => setRisk(e.target.value)}
            style={{ width:'100%', accentColor:C.gold, marginTop:'4px' }} />
          <div style={{ display:'flex', justifyContent:'space-between', fontSize:'10px', color:C.dim, marginTop:'2px' }}>
            <span>1%</span><span style={{ color:C.gold }}>{risk}%</span><span>5%</span>
          </div>
        </Field>
        <Field label="Stock Price ($)" value={sp} onChange={setSp} />
        <Field label="Strike Price ($)" value={st} onChange={setSt} />
        <Field label="Premium Received ($)" value={pr} onChange={setPr} />
      </>}
      right={R ? <>
        <Result label="Maximum Dollar Risk" value={`$${fmtD(R.maxRisk)}`} color={C.gold} sub={`at ${risk}% of $${Number(acct).toLocaleString()}`} />
        <Result label="Maximum Contracts" value={R.maxContracts > 0 ? `${R.maxContracts}` : '< 1'} color={C.text} sub="within your risk tolerance" />
        <Result label="Cash Required (CSP)" value={R.maxContracts > 0 ? `$${fmtD(R.cashReq)}` : '—'} />
        <Result label="% of Account Deployed" value={fmtP(R.deployed)} color={R.deployed > 20 ? C.rose : R.deployed > 10 ? C.gold : C.sage} />
        {R.deployed > 20 && (
          <div style={{ padding:'10px 12px', background:'rgba(196,122,122,0.1)', border:`0.5px solid rgba(196,122,122,0.3)`, borderRadius:'8px', fontSize:'12px', color:C.rose, lineHeight:1.5 }}>
            ⚠ This position is larger than recommended for a single trade
          </div>
        )}
        <div style={{ ...card, background:'rgba(201,168,76,0.06)', fontSize:'12px', color:C.muted, lineHeight:1.6 }}>
          💡 Most professional sellers risk no more than 2–3% of account per trade.
        </div>
      </> : <div style={{ color:C.dim, fontSize:'13px', marginTop:'20px', textAlign:'center' }}>Enter values to see results</div>}
    />
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CALCULATOR 6 — RISK / REWARD
// ─────────────────────────────────────────────────────────────────────────────
function RRChart({ entry, stop, target, dir }) {
  const [E, S, T] = [num(entry), num(stop), num(target)];
  if (!E || !S || !T) return null;
  const all   = [E, S, T];
  const minP  = Math.min(...all) * 0.995;
  const maxP  = Math.max(...all) * 1.005;
  const range = maxP - minP || 1;
  const W = 220, H = 120, PAD = 14;
  const toY = p => PAD + (1 - (p - minP) / range) * (H - PAD * 2);
  const lines = [
    { price: T, color: C.sage, label: `Target $${T.toFixed(2)}` },
    { price: E, color: C.gold, label: `Entry $${E.toFixed(2)}` },
    { price: S, color: C.rose, label: `Stop $${S.toFixed(2)}` },
  ];
  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ background:C.bg3, borderRadius:'8px', display:'block', marginTop:'10px' }}>
      {[T, E, S].map((p, i) => (
        <rect key={i} x={0} y={toY(p) - 1} width={W} height={2}
          fill={[C.sage, C.gold, C.rose][i]} opacity="0.15" />
      ))}
      {lines.map(({ price, color, label }) => (
        <g key={price}>
          <line x1={0} y1={toY(price)} x2={W - 70} y2={toY(price)} stroke={color} strokeWidth="1.5" />
          <text x={W - 68} y={toY(price) + 4} fill={color} fontSize="9" fontFamily="'DM Mono', monospace">{label}</text>
        </g>
      ))}
    </svg>
  );
}

function Calc6() {
  const [entry, setEntry]   = useState('50');
  const [stop,  setStop]    = useState('47');
  const [tgt,   setTgt]     = useState('57');
  const [units, setUnits]   = useState('1');
  const [dir,   setDir]     = useState('long');

  const R = useMemo(() => {
    const [E, S, T, U] = [num(entry), num(stop), num(tgt), num(units)||1];
    if (!E || !S || !T) return null;
    const riskPer   = Math.abs(E - S);
    const rewardPer = Math.abs(T - E);
    const rr        = riskPer > 0 ? rewardPer / riskPer : 0;
    const totalRisk = riskPer * U;
    const totalRew  = rewardPer * U;
    const pass      = rr >= 2;
    return { riskPer, rewardPer, rr, totalRisk, totalRew, pass };
  }, [entry, stop, tgt, units]);

  return (
    <TwoCol
      left={<>
        <div style={{ display:'flex', gap:'8px', marginBottom:'10px' }}>
          {['long', 'short'].map(d => (
            <button key={d} style={btnTab(dir === d)} onClick={() => setDir(d)}>{d === 'long' ? 'Long' : 'Short'}</button>
          ))}
        </div>
        <Field label="Entry Price ($)" value={entry} onChange={setEntry} />
        <Field label="Stop Loss Price ($)" value={stop} onChange={setStop} />
        <Field label="Price Target ($)" value={tgt} onChange={setTgt} />
        <Field label="Shares / Contracts" value={units} onChange={setUnits} min="1" />
        <RRChart entry={entry} stop={stop} target={tgt} dir={dir} />
      </>}
      right={R ? <>
        <Result label="Risk per Share" value={`$${fmtD(R.riskPer)}`} color={C.rose} />
        <Result label="Reward per Share" value={`$${fmtD(R.rewardPer)}`} color={C.sage} />
        <Result label="Risk / Reward Ratio" value={`1 : ${R.rr.toFixed(2)}`}
          color={R.pass ? C.sage : C.rose}
          sub={R.pass ? '✓ Meets the 1:2 minimum' : '✗ Below 1:2 minimum — adjust your target or stop'} />
        <Result label="Total Risk" value={`$${fmtD(R.totalRisk)}`} color={C.rose} />
        <Result label="Total Potential Reward" value={`$${fmtD(R.totalRew)}`} color={C.sage} />
        <div style={{ ...card, background: R.pass ? 'rgba(122,171,138,0.08)' : 'rgba(196,122,122,0.08)',
          border:`0.5px solid ${R.pass ? 'rgba(122,171,138,0.3)' : 'rgba(196,122,122,0.3)'}`, fontSize:'12px', color:C.muted, lineHeight:1.65 }}>
          {R.pass ? '✓ ' : '✗ '}<span style={{ color:C.goldL }}>Lynn: </span>
          "I never take a trade unless I can make at least twice what I risk. If the math doesn't work, I don't trade."
        </div>
      </> : <div style={{ color:C.dim, fontSize:'13px', marginTop:'20px', textAlign:'center' }}>Enter values to see results</div>}
    />
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TAX TRACKER
// ─────────────────────────────────────────────────────────────────────────────
const BRACKETS_SINGLE = [
  { max:11600,  rate:0.10 }, { max:47150,   rate:0.12 }, { max:100525,  rate:0.22 },
  { max:191950, rate:0.24 }, { max:243725,  rate:0.32 }, { max:609350,  rate:0.35 }, { max:Infinity, rate:0.37 },
];
const BRACKETS_MFJ = [
  { max:23200,  rate:0.10 }, { max:94300,   rate:0.12 }, { max:201050,  rate:0.22 },
  { max:383900, rate:0.24 }, { max:487450,  rate:0.32 }, { max:731200,  rate:0.35 }, { max:Infinity, rate:0.37 },
];
const LTCG_SINGLE = [{ max:47025, rate:0 }, { max:518900, rate:0.15 }, { max:Infinity, rate:0.20 }];
const LTCG_MFJ    = [{ max:94050, rate:0 }, { max:583750, rate:0.15 }, { max:Infinity, rate:0.20 }];

const STATE_RATES = {
  'AL':5.0,'AK':0,'AZ':2.5,'AR':4.9,'CA':13.3,'CO':4.4,'CT':6.99,'DE':6.6,
  'FL':0,'GA':5.49,'HI':11.0,'ID':5.8,'IL':4.95,'IN':3.15,'IA':6.0,'KS':5.7,
  'KY':4.5,'LA':4.25,'ME':7.15,'MD':5.75,'MA':5.0,'MI':4.25,'MN':9.85,'MS':5.0,
  'MO':4.95,'MT':6.75,'NE':5.84,'NV':0,'NH':0,'NJ':10.75,'NM':5.9,'NY':10.9,
  'NC':4.75,'ND':2.5,'OH':3.99,'OK':4.75,'OR':9.9,'PA':3.07,'RI':5.99,'SC':6.5,
  'SD':0,'TN':0,'TX':0,'UT':4.65,'VT':8.75,'VA':5.75,'WA':0,'WV':6.5,'WI':7.65,'WY':0,
};
const STATE_NAMES = { 'AL':'Alabama','AK':'Alaska','AZ':'Arizona','AR':'Arkansas','CA':'California','CO':'Colorado','CT':'Connecticut','DE':'Delaware','FL':'Florida','GA':'Georgia','HI':'Hawaii','ID':'Idaho','IL':'Illinois','IN':'Indiana','IA':'Iowa','KS':'Kansas','KY':'Kentucky','LA':'Louisiana','ME':'Maine','MD':'Maryland','MA':'Massachusetts','MI':'Michigan','MN':'Minnesota','MS':'Mississippi','MO':'Missouri','MT':'Montana','NE':'Nebraska','NV':'Nevada','NH':'New Hampshire','NJ':'New Jersey','NM':'New Mexico','NY':'New York','NC':'North Carolina','ND':'North Dakota','OH':'Ohio','OK':'Oklahoma','OR':'Oregon','PA':'Pennsylvania','RI':'Rhode Island','SC':'South Carolina','SD':'South Dakota','TN':'Tennessee','TX':'Texas','UT':'Utah','VT':'Vermont','VA':'Virginia','WA':'Washington','WV':'West Virginia','WI':'Wisconsin','WY':'Wyoming' };

function computeTax(income, brackets) {
  if (income <= 0) return 0;
  let tax = 0, prev = 0;
  for (const b of brackets) {
    if (income <= prev) break;
    const taxable = Math.min(income, b.max) - prev;
    tax += taxable * b.rate;
    prev = b.max;
  }
  return tax;
}

function getMarginalRate(income, brackets) {
  for (const b of brackets) {
    if (income <= b.max) return b.rate * 100;
  }
  return brackets[brackets.length - 1].rate * 100;
}

const QUARTERS = (() => {
  const yr = new Date().getFullYear();
  return [
    { label:'Q1', date: new Date(`${yr}-04-15`) },
    { label:'Q2', date: new Date(`${yr}-06-16`) },
    { label:'Q3', date: new Date(`${yr}-09-15`) },
    { label:'Q4', date: new Date(`${yr + 1}-01-15`) },
  ];
})();

function TaxTracker({ trades = [] }) {
  const [filing,   setFiling]   = useState('single');
  const [w2,       setW2]       = useState('75000');
  const [state,    setState_]   = useState('CA');
  const [stateRate, setStateRate] = useState('13.3');
  const [jarPct,   setJarPct]   = useState('25');
  const [washOpen, setWashOpen] = useState(false);

  const sampleTrades = useMemo(() => [
    { ticker:'AAPL', strategy:'CSP', credit:'2.10', pnl:'210',  status:'Closed - Profit', date:'1/15/2025' },
    { ticker:'KO',   strategy:'CC',  credit:'1.50', pnl:'150',  status:'Closed - Profit', date:'2/10/2025' },
    { ticker:'MSFT', strategy:'CSP', credit:'3.20', pnl:'-180', status:'Closed - Loss',   date:'3/5/2025'  },
    { ticker:'SPY',  strategy:'0DTE',credit:'1.80', pnl:'180',  status:'Closed - Profit', date:'3/20/2025' },
  ], []);

  const activeTrades = trades.length > 0 ? trades : sampleTrades;

  const T = useMemo(() => {
    const closed = activeTrades.filter(t => t.status !== 'Open' && t.pnl);
    const gains   = closed.reduce((s, t) => s + Math.max(0, num(t.pnl)), 0);
    const losses  = closed.reduce((s, t) => s + Math.min(0, num(t.pnl)), 0);
    const net     = gains + losses;
    const stGains = net;
    const ltGains = 0;
    const W2 = num(w2);
    const brackets = filing === 'single' ? BRACKETS_SINGLE : BRACKETS_MFJ;
    const ltcgBrackets = filing === 'single' ? LTCG_SINGLE : LTCG_MFJ;
    const totalIncome = W2 + stGains;
    const taxOnW2 = computeTax(W2, brackets);
    const taxOnAll = computeTax(totalIncome, brackets);
    const stTax = taxOnAll - taxOnW2;
    const ltTax = computeTax(ltGains, ltcgBrackets);
    const stateTax = net > 0 ? net * num(stateRate) / 100 : 0;
    const totalTax = stTax + ltTax + stateTax;
    const effRate = net > 0 ? (totalTax / net) * 100 : 0;
    const marginal = getMarginalRate(totalIncome, brackets);
    const jarAmount = net * num(jarPct) / 100;
    return { gains, losses, net, stGains, ltGains, stTax, ltTax, stateTax, totalTax, effRate, marginal, jarAmount, closedCount: closed.length };
  }, [activeTrades, w2, filing, stateRate, jarPct]);

  const today = new Date();
  const nextQ = QUARTERS.find(q => q.date > today);
  const daysToNextQ = nextQ ? Math.ceil((nextQ.date - today) / (1000 * 60 * 60 * 24)) : 0;

  return (
    <div>
      {/* Tax Profile */}
      <div style={{ ...card, marginBottom:'14px' }}>
        <div style={{ ...eyebrow, marginBottom:'10px' }}>Tax Profile Setup</div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(2, 1fr)', gap:'10px' }}>
          <div>
            <div style={{ fontSize:'11px', color:C.muted, marginBottom:'4px' }}>Filing Status</div>
            <select value={filing} onChange={e => setFiling(e.target.value)} style={inp}>
              <option value="single">Single</option>
              <option value="mfj">Married Filing Jointly</option>
              <option value="mfs">Married Filing Separately</option>
              <option value="hoh">Head of Household</option>
            </select>
          </div>
          <div>
            <div style={{ fontSize:'11px', color:C.muted, marginBottom:'4px' }}>Est. Annual W2/Other Income ($)</div>
            <input type="number" value={w2} onChange={e => setW2(e.target.value)} style={inp} />
          </div>
          <div>
            <div style={{ fontSize:'11px', color:C.muted, marginBottom:'4px' }}>State</div>
            <select value={state} onChange={e => { setState_(e.target.value); setStateRate(String(STATE_RATES[e.target.value] ?? 0)); }} style={inp}>
              {Object.entries(STATE_NAMES).map(([code, name]) => (
                <option key={code} value={code}>{name} {STATE_RATES[code] === 0 ? '(no income tax)' : `(${STATE_RATES[code]}%)`}</option>
              ))}
            </select>
          </div>
          <div>
            <div style={{ fontSize:'11px', color:C.muted, marginBottom:'4px' }}>State Tax Rate (%)</div>
            <input type="number" step="0.1" value={stateRate} onChange={e => setStateRate(e.target.value)} style={inp} />
          </div>
        </div>
        <div style={{ marginTop:'10px', padding:'8px 12px', background:C.bg3, borderRadius:'7px', fontSize:'12px', color:C.muted, display:'flex', gap:'20px', flexWrap:'wrap' }}>
          <span>Marginal Rate: <span style={{ color:C.gold }}>{T.marginal.toFixed(0)}%</span></span>
          <span>Short-Term: <span style={{ color:C.rose }}>taxed as ordinary income</span></span>
          <span>Long-Term: <span style={{ color:C.sage }}>lower LTCG rate (if held 1+ yr)</span></span>
        </div>
      </div>

      {/* 4 Cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(2, 1fr)', gap:'10px', marginBottom:'14px' }}>
        {/* Card 1 */}
        <div style={card}>
          <div style={{ ...eyebrow, marginBottom:'10px' }}>Trading Profits</div>
          {[
            ['Total Gains', `$${fmtD(T.gains)}`, C.sage],
            ['Total Losses', `-$${fmtD(Math.abs(T.losses))}`, C.rose],
            ['Net Trading P&L', `${T.net >= 0 ? '+' : '-'}$${fmtD(Math.abs(T.net))}`, T.net >= 0 ? C.sage : C.rose],
            ['Short-Term Gains', `$${fmtD(T.stGains)}`, C.text],
            ['Long-Term Gains', `$${fmtD(T.ltGains)}`, C.sky],
            ['Trades Analyzed', `${T.closedCount} closed`, C.muted],
          ].map(([l, v, c]) => (
            <div key={l} style={{ display:'flex', justifyContent:'space-between', fontSize:'12px', padding:'4px 0', borderBottom:`0.5px solid rgba(240,237,230,0.04)` }}>
              <span style={{ color:C.muted }}>{l}</span><span style={{ color:c, fontWeight:500 }}>{v}</span>
            </div>
          ))}
        </div>

        {/* Card 2 */}
        <div style={card}>
          <div style={{ ...eyebrow, marginBottom:'10px' }}>Estimated Tax Owed</div>
          {[
            ['Federal (short-term)', `$${fmtD(T.stTax)}`, C.rose],
            ['Federal (long-term)', `$${fmtD(T.ltTax)}`, C.gold],
            ['State Tax', `$${fmtD(T.stateTax)}`, C.muted],
            ['Total Estimated Tax', `$${fmtD(T.totalTax)}`, C.rose],
            ['Effective Rate on Trading', fmtP(T.effRate), C.gold],
          ].map(([l, v, c]) => (
            <div key={l} style={{ display:'flex', justifyContent:'space-between', fontSize:'12px', padding:'4px 0', borderBottom:`0.5px solid rgba(240,237,230,0.04)` }}>
              <span style={{ color:C.muted }}>{l}</span><span style={{ color:c, fontWeight:500 }}>{v}</span>
            </div>
          ))}
        </div>

        {/* Card 3 — The Tax Jar */}
        <div style={card}>
          <div style={{ ...eyebrow, marginBottom:'8px' }}>🏺 The Tax Jar</div>
          <div style={{ marginBottom:'8px' }}>
            <div style={{ display:'flex', justifyContent:'space-between', fontSize:'11px', color:C.muted, marginBottom:'4px' }}>
              <span>Set aside %</span><span style={{ color:C.gold }}>{jarPct}%</span>
            </div>
            <input type="range" min="15" max="40" step="1" value={jarPct} onChange={e => setJarPct(e.target.value)} style={{ width:'100%', accentColor:C.gold }} />
          </div>
          <div style={{ fontSize:'22px', color:C.gold, fontWeight:600, ...serif, marginBottom:'6px' }}>
            ${fmtD(T.jarAmount)}
          </div>
          <div style={{ height:'8px', background:'rgba(255,255,255,0.05)', borderRadius:'4px', overflow:'hidden', marginBottom:'6px' }}>
            <div style={{ height:'100%', width:`${Math.min(num(jarPct), 40) / 40 * 100}%`, background: num(jarPct) >= 30 ? C.sage : num(jarPct) >= 20 ? C.gold : C.rose, borderRadius:'4px', transition:'width 0.4s' }} />
          </div>
          <div style={{ fontSize:'12px', color:C.text, fontWeight:500, marginBottom:'4px' }}>
            Move ${fmtD(T.jarAmount)} to a separate savings account today
          </div>
          <div style={{ fontSize:'11px', color:C.muted, lineHeight:1.5 }}>
            This is your money — but the IRS will want their share. Keeping it separate prevents surprises.
          </div>
        </div>

        {/* Card 4 — Quarterly Payments */}
        <div style={card}>
          <div style={{ ...eyebrow, marginBottom:'10px' }}>Quarterly Estimated Payments</div>
          {QUARTERS.map((q, i) => {
            const isPast = q.date <= today;
            const isNext = q === nextQ;
            const payment = T.totalTax / 4;
            const days = Math.ceil((q.date - today) / (1000 * 60 * 60 * 24));
            return (
              <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'6px 8px', borderRadius:'6px', marginBottom:'4px', background: isNext ? 'rgba(201,168,76,0.1)' : 'transparent', border:`0.5px solid ${isNext ? 'rgba(201,168,76,0.3)' : 'transparent'}` }}>
                <div>
                  <div style={{ fontSize:'12px', color: isNext ? C.gold : isPast ? C.dim : C.muted, fontWeight: isNext ? 600 : 400 }}>
                    {q.label} — {q.date.toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' })}
                  </div>
                  <div style={{ fontSize:'10px', color:C.dim }}>
                    {isPast ? 'Past' : `${days} days away`}
                    {isNext && ' ← Next due'}
                  </div>
                </div>
                <span style={{ color: isNext ? C.gold : C.muted, fontSize:'12px', fontWeight:500 }}>${fmtD(payment)}</span>
              </div>
            );
          })}
          <div style={{ marginTop:'6px', fontSize:'11px', color:C.dim, lineHeight:1.5 }}>
            If you expect to owe more than $1,000 in taxes, the IRS requires quarterly estimated payments to avoid penalties.
          </div>
        </div>
      </div>

      {/* Wash Sale Education */}
      <div style={{ ...card, marginBottom:'10px' }}>
        <button style={{ background:'none', border:'none', cursor:'pointer', width:'100%', display:'flex', justifyContent:'space-between', alignItems:'center', padding:0 }} onClick={() => setWashOpen(o => !o)}>
          <div style={{ ...eyebrow }}>Understanding Wash Sale Rules</div>
          <span style={{ fontSize:'12px', color:C.dim }}>{washOpen ? '▲' : '▼'}</span>
        </button>
        {washOpen && (
          <div style={{ marginTop:'12px', fontSize:'13px', color:C.muted, lineHeight:1.75 }}>
            <p style={{ marginBottom:'10px' }}><strong style={{ color:C.text }}>What is a wash sale?</strong> Selling a security at a loss and buying the same or substantially identical security within 30 days before or after the sale. The disallowed loss is added to your cost basis of the replacement shares.</p>
            <p style={{ marginBottom:'10px' }}><strong style={{ color:C.text }}>The Wheel and wash sales:</strong> If you get assigned shares and sell them at a loss, be careful about selling puts on the same stock within 30 days. That could trigger a wash sale on your stock loss.</p>
            <p style={{ marginBottom:'10px' }}><strong style={{ color:C.text }}>Options premiums are different:</strong> Selling a put and having it expire worthless is <em>not</em> a wash sale — you never sold a security at a loss.</p>
            <p style={{ marginBottom:'10px' }}><strong style={{ color:C.text }}>The safe harbor:</strong> Wait 31 days before re-entering a position you sold at a loss.</p>
            <div style={{ padding:'10px 14px', background:'rgba(201,168,76,0.07)', borderLeft:`2px solid ${C.gold}`, borderRadius:'0 6px 6px 0', marginTop:'4px' }}>
              <span style={{ color:C.goldL }}>Lynn: </span>
              <span>"I always track my cost basis carefully. The Wheel works best when you're disciplined about which stocks you wheel and when."</span>
            </div>
          </div>
        )}
      </div>

      {/* Disclaimer */}
      <div style={{ padding:'12px 16px', border:`0.5px solid rgba(196,122,122,0.3)`, borderRadius:'8px', fontSize:'11px', color:C.dim, lineHeight:1.7 }}>
        ⚠ This tax estimator is for educational purposes only and provides rough estimates based on simplified tax calculations. Your actual tax liability will depend on your complete financial picture including deductions, credits, other income, and applicable state laws. Please consult a qualified CPA or tax professional before making financial decisions. <strong>This is not tax advice.</strong>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN — TOOLS HUB
// ─────────────────────────────────────────────────────────────────────────────
const CALC_TABS = [
  { id:'pnl',  label:'P&L',           component: Calc1 },
  { id:'cc',   label:'Covered Call',  component: Calc2 },
  { id:'csp',  label:'Cash-Sec Put',  component: Calc3 },
  { id:'wheel',label:'The Wheel',     component: Calc4 },
  { id:'size', label:'Position Size', component: Calc5 },
  { id:'rr',   label:'Risk/Reward',   component: Calc6 },
];

export default function ToolsHub({ trades = [], onBack, xpAward }) {
  const [mainTab,  setMainTab]  = useState('calc');
  const [calcTab,  setCalcTab]  = useState('pnl');

  const ActiveCalc = CALC_TABS.find(t => t.id === calcTab)?.component;

  return (
    <div style={{ padding:'24px', background:C.bg, minHeight:'100vh' }}>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', gap:'12px', marginBottom:'20px' }}>
        {onBack && <button style={{ padding:'8px 14px', background:'transparent', border:`0.5px solid ${C.border}`, borderRadius:'7px', color:C.muted, fontSize:'12px', cursor:'pointer' }} onClick={onBack}>← Back</button>}
        <div>
          <div style={{ ...eyebrow, marginBottom:'2px' }}>Practice Tools</div>
          <h2 style={{ ...serif, fontSize:'26px', fontWeight:500, color:C.text, margin:0 }}>Tools & Tax Hub</h2>
        </div>
      </div>

      {/* Main tabs */}
      <div style={{ display:'flex', gap:'8px', marginBottom:'20px', borderBottom:`0.5px solid ${C.border}`, paddingBottom:'12px' }}>
        <button style={btnTab(mainTab === 'calc')} onClick={() => setMainTab('calc')}>⊞ Calculators</button>
        <button style={btnTab(mainTab === 'tax')}  onClick={() => setMainTab('tax')}>🏺 Tax Tracker</button>
      </div>

      {mainTab === 'calc' && (
        <div>
          {/* Calculator sub-tabs */}
          <div style={{ display:'flex', gap:'6px', flexWrap:'wrap', marginBottom:'16px' }}>
            {CALC_TABS.map(t => (
              <button key={t.id} style={btnTab(calcTab === t.id)} onClick={() => setCalcTab(t.id)}>{t.label}</button>
            ))}
          </div>
          <div style={{ background:C.bg2, border:`0.5px solid ${C.border}`, borderRadius:'12px', padding:'20px' }}>
            <div style={{ ...eyebrow, marginBottom:'14px' }}>{CALC_TABS.find(t => t.id === calcTab)?.label}</div>
            {ActiveCalc && <ActiveCalc />}
          </div>
        </div>
      )}

      {mainTab === 'tax' && (
        <TaxTracker trades={trades} />
      )}
    </div>
  );
}
